/**
 * Performance › Segments (`/performance/segments`) — audiences the user
 * names, and the Core Web Vitals each one actually gets.
 *
 * Called "segments" in the UI, `traffic_profiles` in the database and on
 * the API path. Same split the meter already uses ("audits" / "events"
 * to the customer, `snapshots_*` in the data plane): segment is the
 * standard analytics word for a filtered slice of traffic, and
 * "profile" was already taken by `/settings/profile` — the account one.
 *
 * Replaces the auto-derived cohort list, which grouped by a fixed
 * (device x connection x data-saver) key: it could only ever show those
 * combinations, and the user couldn't name the audience they actually
 * cared about. Segments are now rows in `traffic_profiles`, seeded with
 * four starters when a site is created.
 *
 * This page is the index — one summary row per segment, in the user's
 * saved order, each linking into `./$segmentUuid` for the full picture.
 * The card shows only the three Core Web Vitals on purpose; the detail
 * page owns TTFB / FCP, the comparison against everyone, the trend, and
 * the per-value split.
 *
 * Each card fetches its own vitals through the existing filtered perf
 * endpoint. That endpoint groups by form factor, so a segment that pins
 * one device shows one line and a broader one shows a line per device —
 * deliberately, rather than collapsing them into a single figure.
 * Averaging per-device p75s is not a percentile of anything.
 *
 * Segments whose filters the deployed pipe can't apply say so instead of
 * rendering an empty card. See `resolution` in lib/queries/traffic-profiles.
 *
 * Lives under Performance, not Traffic, because what it reports is Core
 * Web Vitals — Traffic answers "who came and how many". The segment
 * definition itself is lens-agnostic (the `traffic_profiles` table knows
 * nothing about vitals), so a Traffic-side view over the same segments is
 * a sensible later addition. It isn't buildable yet: the traffic KPI pipe
 * reads `pixel_daily_kpi_mv`, which is keyed only by
 * (org, site, form_factor, day) and so can segment by device and nothing
 * else. That needs a filtered traffic pipe mirroring
 * `pixel_site_perf_filtered` before it's worth a surface.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { FilterBar } from '@/components/primitives/FilterBar';
import { type PageRange, PageRangeSelect } from '@/components/primitives/PageRangePill';
import { PlaneBadge, RUM_EXPLAINER } from '@/components/rum/PerformanceSection';
import { ProfileEditorDialog } from '@/components/rum/ProfileEditorDialog';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveSiteUuid } from '@/lib/active-site';
import { bandText, clsBand, formatCls, formatMs, inpBand, lcpBand } from '@/lib/format';
import { type PixelPerfRow, useSitePixelPerf } from '@/lib/queries/pixel';
import {
  PROFILE_DIMENSIONS,
  type TrafficProfile,
  dimensionLabel,
  profileFiltersToPerfOpts,
  useDeleteTrafficProfile,
  useTrafficProfiles,
} from '@/lib/queries/traffic-profiles';
import { DEVICE_LABELS, SEGMENT_DEFAULT_RANGE, describeValues } from '@/lib/segments';
import { cn } from '@/lib/utils';
import { Devices, DotsThree, Plus } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/performance/segments/')({
  component: SegmentsPage,
});

function SegmentsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const [range, setRange] = useState<PageRange>(SEGMENT_DEFAULT_RANGE);
  const [editing, setEditing] = useState<TrafficProfile | undefined>(undefined);
  const [editorOpen, setEditorOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TrafficProfile | null>(null);

  const segments = useTrafficProfiles(siteUuid);
  const del = useDeleteTrafficProfile();
  const rows = segments.data ?? [];

  function openCreate() {
    setEditing(undefined);
    setEditorOpen(true);
  }

  return (
    <SiteIsland section="Segments" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && (
        <div className="space-y-4">
          {/* Pinned strip, same as the other Performance planes (#537):
              the window scopes every card below, so it has to stay
              answerable once they've scrolled away. There are no
              audience pickers here — a segment IS the audience — so the
              left edge carries the plane badge instead. */}
          <FilterBar>
            {/* Same badge + explainer as the Performance overview's
                real-user card. Everything on this surface is pixel RUM,
                and the tooltip is where "RUM" gets spelled out. */}
            <PlaneBadge label="Real users · RUM" tooltip={RUM_EXPLAINER} />
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <PageRangeSelect value={range} onChange={setRange} />
              <Button size="sm" onClick={openCreate}>
                <Plus aria-hidden="true" size={14} /> New segment
              </Button>
            </div>
          </FilterBar>

          <p className="text-sm text-[var(--color-mute)]">
            Audiences you've named, and how the site performs for each.
          </p>

          {segments.isLoading && <Skeleton className="h-28 w-full rounded-xl" />}

          {!segments.isLoading && rows.length === 0 && (
            <EmptyState
              icon={Devices}
              title="No segments yet"
              description="A segment is an audience worth watching on its own — phones on slow connections, visitors from one market. Name one and its Core Web Vitals show up here."
              action={<Button onClick={openCreate}>Create a segment</Button>}
            />
          )}

          {rows.map((segment) => (
            <SegmentCard
              key={segment.uuid}
              segment={segment}
              siteUuid={siteUuid}
              range={range}
              onEdit={() => {
                setEditing(segment);
                setEditorOpen(true);
              }}
              onDelete={() => setPendingDelete(segment)}
            />
          ))}

          <ProfileEditorDialog
            siteUuid={siteUuid}
            profile={editing}
            open={editorOpen}
            onOpenChange={setEditorOpen}
          />

          <ConfirmDialog
            open={pendingDelete !== null}
            onOpenChange={(next) => {
              if (!next) setPendingDelete(null);
            }}
            title={`Delete "${pendingDelete?.name ?? ''}"?`}
            description="The segment stops appearing here. Nothing about the underlying traffic data changes."
            confirmLabel="Delete"
            destructive
            loading={del.isPending}
            onConfirm={async () => {
              if (!pendingDelete) return;
              await del.mutateAsync({ uuid: pendingDelete.uuid, siteUuid });
              setPendingDelete(null);
            }}
          />
        </div>
      )}
    </SiteIsland>
  );
}

function SegmentCard({
  segment,
  siteUuid,
  range,
  onEdit,
  onDelete,
}: {
  segment: TrafficProfile;
  siteUuid: string;
  range: PageRange;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  const ready = segment.resolution === 'ready';
  const f = segment.filters;

  // One query per card. Bounded by how many segments the user made, and
  // skipped for segments the pipe can't answer — firing it there would
  // return numbers for a wider audience than the segment describes.
  const perf = useSitePixelPerf(siteUuid, {
    days: range,
    ...profileFiltersToPerfOpts(f),
    enabled: ready,
  });

  const perfRows = (perf.data ?? []).filter((r) => r.lcp_sample_count > 0);
  const pendingLabels = Object.keys(f)
    .filter((k) => !PROFILE_DIMENSIONS.find((d) => d.key === k)?.ready)
    .map(dimensionLabel);

  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {/* The name is the link into the detail page. The card
                isn't clickable as a whole — the kebab menu lives
                inside it, and nested interactive elements aren't
                valid. */}
            <Link
              to="/performance/segments/$segmentUuid"
              params={{ segmentUuid: segment.uuid }}
              className="truncate text-sm font-medium text-[var(--color-ink)] underline-offset-4 outline-none hover:underline focus-visible:underline"
            >
              {segment.name}
            </Link>
          </div>
          {segment.description && (
            <p className="mt-0.5 text-xs text-[var(--color-mute)]">{segment.description}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(f).map(([dimension, values]) => (
              <span
                key={dimension}
                className="rounded-md bg-[var(--color-paper-alt)] px-2 py-0.5 text-[11px] text-[var(--color-ink-soft)]"
              >
                <span className="text-[var(--color-mute)]">{dimensionLabel(dimension)}: </span>
                {describeValues(dimension, values ?? [])}
              </span>
            ))}
          </div>
        </div>
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${segment.name}`} />
            }
          >
            <DotsThree aria-hidden="true" size={16} />
          </MenuTrigger>
          <MenuPopup>
            <MenuItem onClick={onEdit}>Edit</MenuItem>
            <MenuItem onClick={onDelete}>Delete</MenuItem>
          </MenuPopup>
        </Menu>
      </header>

      <div className="border-t border-[var(--color-line-soft)] px-4 py-3">
        {!ready ? (
          <p className="text-xs text-[var(--color-mute)]">
            Vitals aren't available for this segment yet — reporting doesn't cover{' '}
            {pendingLabels.join(', ')}. It's saved, and will fill in once it does.
          </p>
        ) : perf.isLoading ? (
          <Skeleton className="h-6 w-2/3" />
        ) : perfRows.length === 0 ? (
          <p className="text-xs text-[var(--color-mute)]">
            No visitors matched this segment in the last {range} days.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {perfRows.map((row) => (
              <PerfLine key={row.form_factor} row={row} showDevice={perfRows.length > 1} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** One device's vitals. The device label only appears when the segment
 *  spans more than one — on a phone-only segment it'd be noise. */
function PerfLine({ row, showDevice }: { row: PixelPerfRow; showDevice: boolean }): ReactElement {
  return (
    <li className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
      {showDevice && (
        <span className="w-16 shrink-0 text-xs text-[var(--color-mute)]">
          {DEVICE_LABELS[row.form_factor] ?? row.form_factor}
        </span>
      )}
      <Metric label="LCP" value={formatMs(row.lcp_p75)} band={lcpBand(row.lcp_p75)} />
      <Metric label="CLS" value={formatCls(row.cls_p75)} band={clsBand(row.cls_p75)} />
      <Metric label="INP" value={formatMs(row.inp_p75)} band={inpBand(row.inp_p75)} />
      <span className="text-xs text-[var(--color-mute)]">
        {row.lcp_sample_count.toLocaleString()} samples
      </span>
    </li>
  );
}

function Metric({
  label,
  value,
  band,
}: {
  label: string;
  value: string;
  band: ReturnType<typeof lcpBand>;
}): ReactElement {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </span>
      <span className={cn('font-medium tabular-nums', bandText(band))}>{value}</span>
    </span>
  );
}
